<template>
  <section class="AZ-banner-section" :class="sectionClass">
    <div class="container">
      <div class="row" :class="rowClass">
        <div v-if="loading" class="col-lg-12 text-center py-3">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">در حال بارگذاری...</span>
          </div>
        </div>
        
        <template v-else-if="banners.length > 0">
          <div 
            v-for="banner in banners" 
            :key="banner.id" 
            :class="colClass"
          >
            <component
              :is="!clickOnly && banner.link_url ? 'a' : 'div'"
              :href="!clickOnly && banner.link_url ? banner.link_url : undefined"
              :target="!clickOnly && banner.link_url ? '_blank' : undefined"
              :rel="!clickOnly && banner.link_url ? 'noopener noreferrer' : undefined"
              :class="['banner-box', bannerClass, 'AZ-img-container']"
              @click="handleBannerClick(banner)"
            >
              <div class="AZ-img-container-inner AZ-img-cover">
                <img 
                  :src="banner.image_url" 
                  :alt="banner.title || 'Banner'" 
                  @error="handleImageError"
                />
              </div>
            </component>
          </div>
        </template>

        <!-- Fallback -->
        <div v-else-if="showFallback" :class="colClass">
          <component
            :is="!clickOnly && fallbackLink ? 'a' : 'div'"
            :href="!clickOnly && fallbackLink ? fallbackLink : undefined"
            :class="['banner-box', bannerClass, 'AZ-img-container']"
            @click="handleFallbackClick"
          >
            <div class="AZ-img-container-inner AZ-img-cover">
              <img :src="fallbackImage" :alt="fallbackTitle || 'Banner'" />
            </div>
          </component>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { bannerAPI } from '@/api'

export default {
  name: 'PromoBanner',
  emits: ['banner-click'],
  props: {
    // نوع بنر برای دریافت از API
    type: {
      type: String,
      required: true
    },
    // کلاس سفارشی برای section
    sectionClass: {
      type: String,
      default: ''
    },
    // کلاس سفارشی برای row
    rowClass: {
      type: String,
      default: ''
    },
    // کلاس سفارشی برای col
    colClass: {
      type: String,
      default: 'col-lg-12'
    },
    // کلاس سفارشی برای banner-box
    bannerClass: {
      type: String,
      default: ''
    },
    // نمایش fallback در صورت عدم وجود بنر
    showFallback: {
      type: Boolean,
      default: false
    },
    // تصویر fallback
    fallbackImage: {
      type: String,
      default: '/assets/img/banner.png'
    },
    // عنوان fallback
    fallbackTitle: {
      type: String,
      default: ''
    },
    // توضیحات fallback
    fallbackDescription: {
      type: String,
      default: ''
    },
    // لینک fallback
    fallbackLink: {
      type: String,
      default: ''
    },
    clickOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      banners: [],
      loading: false,
      error: null
    }
  },
  mounted() {
    this.fetchBanners()
  },
  methods: {
    async fetchBanners() {
      this.loading = true
      this.error = null
      
      try {
        const response = await bannerAPI.getByType(this.type)
        if (response.data && response.data.data) {
          this.banners = response.data.data
          
          // Track views for all banners
          this.banners.forEach(banner => {
            this.trackView(banner.id)
          })
        }
      } catch (error) {
        console.error('Error fetching banners:', error)
        this.error = error
      } finally {
        this.loading = false
      }
    },
    handleBannerClick(banner) {
      // Track click
      this.trackClick(banner.id)
      
      // Emit event for parent component
      this.$emit('banner-click', banner)
    },
    handleFallbackClick() {
      this.$emit('banner-click', {
        id: null,
        type: this.type,
        link_url: this.fallbackLink || '',
        isFallback: true
      })
    },
    async trackView(bannerId) {
      try {
        await bannerAPI.trackView(bannerId)
      } catch (error) {
        console.debug('Banner view tracking failed:', error)
      }
    },
    async trackClick(bannerId) {
      try {
        await bannerAPI.trackClick(bannerId)
      } catch (error) {
        console.debug('Banner click tracking failed:', error)
      }
    },
    handleImageError(event) {
      // Try fallback image
      if (this.fallbackImage && event.target.src !== this.fallbackImage) {
        event.target.src = this.fallbackImage
      }
    }
  }
}
</script>

<style scoped>
.spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>
